/*!
 * Copyright © 2005-2025 Hyland Software, Inc. and its affiliates. All rights reserved.
 *
 * Alfresco Example Content Application
 *
 * This file is part of the Alfresco Example Content Application.
 * If the software was purchased under a paid Alfresco license, the terms of
 * the paid license agreement will prevail. Otherwise, the software is
 * provided under the following open source license terms:
 *
 * The Alfresco Example Content Application is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Lesser General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * The Alfresco Example Content Application is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Lesser General Public License for more details.
 *
 * You should have received a copy of the GNU Lesser General Public License
 * from Hyland Software. If not, see <http://www.gnu.org/licenses/>.
 */

import { inject, Injectable } from '@angular/core';
import { AlfrescoApiService } from '@alfresco/adf-content-services';
import { SearchApi } from '@alfresco/js-api';
import { from, Observable, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { TemplateInstance, WorkflowService } from '../workflow/workflow.service';

/** One value of a field and how many documents carry it; `''` is "not set". */
export interface Bucket {
  value: string;
  count: number;
}

export interface DocumentInsights {
  /** Documents that carry DMS details, as far as they were read. */
  total: number;
  /** True when there were more than {@link MAX_DOCUMENTS}; the figures cover the newest ones. */
  truncated: boolean;
  byStatus: Bucket[];
  byDepartment: Bucket[];
  byType: Bucket[];
  byLevel: Bucket[];
}

export interface MonthCount {
  /** `YYYY-MM`, the month's first day in the reader's own time. */
  month: string;
  count: number;
}

/** How long one kind of flow takes from start to finish. */
export interface FlowTiming {
  /** A template's name, or the engine's definition key for a flow started without one. */
  name: string;
  /** True when {@link name} is a definition key the page translates. */
  builtin: boolean;
  completed: number;
  averageMs: number;
  medianMs: number;
}

export interface OpenFlow {
  name: string;
  builtin: boolean;
  /** The process to open — the current step's, for a template flow. */
  processId: string;
  startedAt: string;
}

export interface WorkflowInsights {
  running: number;
  completed: number;
  cancelled: number;
  averageMs: number | null;
  medianMs: number | null;
  byFlow: FlowTiming[];
  /** The longest-running open flows, oldest first. */
  oldest: OpenFlow[];
  truncated: boolean;
  /**
   * The engine would not list finished processes, so only open ones are
   * counted — the timings are missing, not zero. It happens when a process's
   * definition was removed after it ran: the whole history query then fails.
   */
  historyMissing?: boolean;
}

/** Enough for a real repository; the page says when the figures were cut. */
export const MAX_DOCUMENTS = 10000;
const DOCUMENT_PAGE = 1000;
const MAX_PROCESSES = 2000;
const PROCESS_PAGE = 500;
const MONTHS = 12;
const OLDEST = 5;

/** Documents a person filed — not thumbnails, and not what the repository installed itself. */
const UPLOADS = 'TYPE:"cm:content" AND NOT TYPE:"cm:thumbnail" AND NOT TYPE:"cm:failedThumbnail" AND NOT creator:"System"';

/** Documents with DMS details. The exact-match `ASPECT:` form is answered from the metadata, never the lagging index. */
const FILED = 'TYPE:"cm:content" AND ASPECT:"dms:documentInfo"';

/**
 * The figures behind the organisation overview.
 *
 * Everything is asked with the reader's own credentials, so each person sees
 * totals over what they may read — the whole repository for an administrator.
 *
 * The DMS fields cannot be counted by the search service's facets here: they
 * are not in the full-text index, which is what facets read, and a facet on
 * them answers nothing. So the documents that carry DMS details are read a
 * page at a time, their four fields only, and counted in the browser. Uploads
 * per month are plain dates, which the index does hold, so those are facet
 * queries — one request for the whole year.
 */
@Injectable({ providedIn: 'root' })
export class InsightsService {
  private readonly api = inject(AlfrescoApiService);
  private readonly workflow = inject(WorkflowService);

  private get searchApi(): SearchApi {
    return new SearchApi(this.api.getInstance() as any);
  }

  private wfCall(path: string, query: any): Observable<any> {
    const inst: any = this.api.getInstance();
    return from(
      inst.discoveryClient.callApi(
        `/-default-/public/workflow/versions/1${path}`,
        'GET',
        {},
        query,
        {},
        {},
        null,
        ['application/json'],
        ['application/json']
      ) as Promise<any>
    );
  }

  documents(): Observable<DocumentInsights> {
    const status = new Map<string, number>();
    const department = new Map<string, number>();
    const type = new Map<string, number>();
    const level = new Map<string, number>();
    const add = (counts: Map<string, number>, value: unknown) => {
      const key = typeof value === 'string' ? value.trim() : '';
      counts.set(key, (counts.get(key) ?? 0) + 1);
    };

    const read = (skipCount: number, seen: number): Observable<DocumentInsights> =>
      from(
        this.searchApi.search({
          query: { query: FILED, language: 'afts' },
          paging: { skipCount, maxItems: DOCUMENT_PAGE },
          include: ['properties'],
          fields: ['id', 'properties'],
          sort: [{ type: 'FIELD', field: 'cm:created', ascending: false }]
        } as any) as Promise<any>
      ).pipe(
        switchMap((res) => {
          const entries: any[] = res?.list?.entries ?? [];
          for (const { entry } of entries) {
            const props = entry?.properties ?? {};
            add(status, props['dms:status']);
            add(department, props['dms:department']);
            add(type, props['dms:documentType']);
            add(level, props['dms:confidentiality']);
          }
          const total = seen + entries.length;
          const more = !!res?.list?.pagination?.hasMoreItems && entries.length > 0;
          if (more && total < MAX_DOCUMENTS) {
            return read(total, total);
          }
          return of({
            total,
            truncated: more,
            byStatus: InsightsService.sorted(status),
            byDepartment: InsightsService.sorted(department),
            byType: InsightsService.sorted(type),
            byLevel: InsightsService.sorted(level)
          });
        })
      );

    return read(0, 0);
  }

  /** Documents filed in each of the last twelve months, oldest first, this month last. */
  uploadsByMonth(now = new Date()): Observable<MonthCount[]> {
    const months = Array.from({ length: MONTHS }, (_, i) => new Date(now.getFullYear(), now.getMonth() - (MONTHS - 1) + i, 1));
    const label = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    const stamp = (d: Date) => `${label(d)}-01T00:00:00`;

    return from(
      this.searchApi.search({
        query: { query: UPLOADS, language: 'afts' },
        paging: { maxItems: 1 },
        facetQueries: months.map((start) => {
          const end = new Date(start.getFullYear(), start.getMonth() + 1, 1);
          return { query: `cm:created:[${stamp(start)} TO ${stamp(end)}>`, label: label(start) };
        })
      } as any) as Promise<any>
    ).pipe(
      map((res) => {
        const counts = new Map<string, number>(
          (res?.list?.context?.facetQueries ?? []).map((q: any) => [String(q.label), Number(q.count) || 0] as [string, number])
        );
        return months.map((d) => ({ month: label(d), count: counts.get(label(d)) ?? 0 }));
      })
    );
  }

  /**
   * Flows and how long they take.
   *
   * A template flow runs each of its steps as its own engine process, so it is
   * timed as a whole — from its start to its last step — and its processes are
   * not counted again on their own. Any other process is one flow. A process
   * ended through the API rather than by its last task was cancelled, and is
   * left out of the timings.
   */
  workflows(now = new Date()): Observable<WorkflowInsights> {
    return this.workflow.listInstances().pipe(
      catchError(() => of([] as TemplateInstance[])),
      switchMap((instances) =>
        this.processes("(status='any')").pipe(
          map((result) => InsightsService.summarise(instances, result.processes, result.truncated, now)),
          // one broken record makes the engine refuse the whole history; the open flows still count
          catchError(() =>
            this.processes("(status='active')").pipe(
              map((result) => ({ ...InsightsService.summarise(instances, result.processes, result.truncated, now), historyMissing: true }))
            )
          )
        )
      )
    );
  }

  private processes(where: string): Observable<{ processes: any[]; truncated: boolean }> {
    const read = (skipCount: number, acc: any[]): Observable<{ processes: any[]; truncated: boolean }> =>
      this.wfCall('/processes', { skipCount, maxItems: PROCESS_PAGE, where }).pipe(
        switchMap((res) => {
          const entries = (res?.list?.entries ?? []).map((e: any) => e.entry);
          const all = acc.concat(entries);
          const more = !!res?.list?.pagination?.hasMoreItems && entries.length > 0;
          if (more && all.length < MAX_PROCESSES) {
            return read(all.length, all);
          }
          return of({ processes: all, truncated: more });
        })
      );
    return read(0, []);
  }

  static summarise(instances: TemplateInstance[], processes: any[], truncated: boolean, now: Date): WorkflowInsights {
    const inTemplate = new Set<string>();
    for (const inst of instances) {
      for (const id of [...(inst.processIds ?? []), inst.processId]) {
        if (id) {
          inTemplate.add(id);
        }
      }
    }

    const timings = new Map<string, { builtin: boolean; durations: number[] }>();
    const time = (name: string, builtin: boolean, ms: number) => {
      const entry = timings.get(name) ?? { builtin, durations: [] };
      entry.durations.push(ms);
      timings.set(name, entry);
    };
    const open: OpenFlow[] = [];
    let running = 0;
    let completed = 0;
    let cancelled = 0;

    for (const inst of instances) {
      if (inst.status === 'cancelled') {
        cancelled++;
      } else if (inst.status === 'completed') {
        completed++;
        const last = inst.history?.length ? inst.history[inst.history.length - 1].at : '';
        const ms = Date.parse(last) - Date.parse(inst.startedAt);
        if (ms >= 0) {
          time(inst.templateName, false, ms);
        }
      } else {
        running++;
        open.push({ name: inst.templateName, builtin: false, processId: inst.processId ?? '', startedAt: inst.startedAt });
      }
    }

    for (const p of processes) {
      if (!p?.id || inTemplate.has(p.id)) {
        continue;
      }
      const key = String(p.processDefinitionKey ?? p.processDefinitionId ?? '').split(':')[0];
      if (!p.endedAt && !p.completed) {
        running++;
        open.push({ name: key, builtin: true, processId: p.id, startedAt: p.startedAt });
      } else if (p.deleteReason) {
        cancelled++;
      } else {
        completed++;
        const ms = typeof p.durationInMs === 'number' ? p.durationInMs : Date.parse(p.endedAt) - Date.parse(p.startedAt);
        if (ms >= 0) {
          time(key, true, ms);
        }
      }
    }

    const all = [...timings.values()].flatMap((t) => t.durations);
    const byFlow: FlowTiming[] = [...timings.entries()]
      .map(([name, t]) => ({
        name,
        builtin: t.builtin,
        completed: t.durations.length,
        averageMs: InsightsService.average(t.durations),
        medianMs: InsightsService.median(t.durations)
      }))
      .sort((a, b) => b.completed - a.completed || a.name.localeCompare(b.name));

    const age = (flow: OpenFlow) => now.getTime() - (Date.parse(flow.startedAt) || now.getTime());
    return {
      running,
      completed,
      cancelled,
      averageMs: all.length ? InsightsService.average(all) : null,
      medianMs: all.length ? InsightsService.median(all) : null,
      byFlow,
      oldest: open.sort((a, b) => age(b) - age(a)).slice(0, OLDEST),
      truncated
    };
  }

  /** Most first; "not set" last whatever its size, since it is a gap to close, not a group. */
  private static sorted(counts: Map<string, number>): Bucket[] {
    return [...counts.entries()]
      .map(([value, count]) => ({ value, count }))
      .sort((a, b) => ((a.value === '') !== (b.value === '') ? (a.value === '' ? 1 : -1) : b.count - a.count || a.value.localeCompare(b.value)));
  }

  private static average(values: number[]): number {
    return values.reduce((sum, v) => sum + v, 0) / values.length;
  }

  private static median(values: number[]): number {
    const sorted = [...values].sort((a, b) => a - b);
    const mid = Math.floor(sorted.length / 2);
    return sorted.length % 2 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2;
  }
}
